import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from '@tanstack/react-query';
import { SessionImageDTO } from '../../../../api/generated';
import { ApiError } from '../../../ApiLoadingGuard/ApiLoadingGuard';
import { WrapperCardWithTitle } from '../../../../layouts/WrapperCardWithTitle';
import { SelfServiceImageCard } from './SelfServiceImageCard';
import ProcessingTimeoutAlert from './ProcessingTimeoutAlert';

interface OcInsightsImageProps {
  selectedImageIndex: number | null;
  isNoImageCorrect: boolean;
  isImageTimedOut: boolean;
  image: UseQueryResult<SessionImageDTO, ApiError>;
  processingCount: number;
  onImageChangeRequest: () => void;
}

export function OcInsightsImage({
  selectedImageIndex,
  isNoImageCorrect,
  isImageTimedOut,
  image,
  processingCount,
  onImageChangeRequest,
}: OcInsightsImageProps): ReactElement {
  const { t } = useTranslation();

  return (
    <WrapperCardWithTitle
      title='loadingArea.title'
      elevation={0}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignContent: 'center',
        justifyContent: 'center',
      }}
    >
      <Box display='flex' flexDirection='column' flex={1} padding={2} gap={2}>
        {selectedImageIndex === null ? (
          <Stack flex={1} minHeight={0} alignItems='center' justifyContent='center' gap={2}>
            {isImageTimedOut ? <ProcessingTimeoutAlert /> : <CircularProgress />}
            <Typography>{t('selfService.waitingForImages')}</Typography>
            {processingCount > 0 && (
              <Typography variant='body2' color='text.secondary'>
                {t('selfService.imageSelector.processingHint', { count: processingCount })}
              </Typography>
            )}
          </Stack>
        ) : (
          <Stack flex={1} minHeight={0} gap={2} justifyContent={isNoImageCorrect ? 'center' : 'flex-start'}>
            {!isNoImageCorrect && <SelfServiceImageCard imageQuery={image} />}
            <Button variant='outlined' onClick={onImageChangeRequest}>
              {t('selfService.openImageSelection')}
            </Button>
          </Stack>
        )}
      </Box>
    </WrapperCardWithTitle>
  );
}
